/**
 * Bundled demo assembly for local UI / offline workshop walkthrough.
 * Assets live under public/demo/ (served at /demo/…).
 *
 * Engine upload (MinIO) is optional: when S3 is not configured the SPA still
 * loads BOM + Reports + a pre-tessellated mesh for the 3D viewer.
 */
import { enrichForecast } from "./forecast";
import { buildE2eMockForecast } from "./e2eFixture";
import type { ForecastState } from "../types";

export const DEMO_STEP_URL = "/demo/slicrank-mechanism.step";
export const DEMO_MESH_URL = "/demo/slicrank-mesh.json";
/** Display name matches e2e mock + original fixture. */
export const DEMO_STEP_NAME = "SLICRANK MECHANISM.STEP";
export const DEMO_DRAWING_ID = "demo-slicrank";

export type DemoMeshPayload = {
  vertices: number[];
  normals?: number[];
  indices: number[];
  colors?: number[];
  groups?: Array<{
    part_index: number;
    name?: string;
    start: number;
    count: number;
    solid_index?: number;
  }>;
  color_source?: string;
};

export async function fetchDemoStepFile(): Promise<File> {
  const res = await fetch(DEMO_STEP_URL);
  if (!res.ok) {
    throw new Error(`Demo STEP missing (${res.status}): ${DEMO_STEP_URL}`);
  }
  const blob = await res.blob();
  return new File([blob], DEMO_STEP_NAME, { type: "application/octet-stream" });
}

export async function fetchDemoMesh(): Promise<DemoMeshPayload | null> {
  try {
    const res = await fetch(DEMO_MESH_URL);
    if (!res.ok) return null;
    return (await res.json()) as DemoMeshPayload;
  } catch {
    return null;
  }
}

/** Offline forecast + optional OCC mesh for Viewer / BOM / Reports. */
export async function loadDemoForecast(): Promise<ForecastState> {
  const raw = buildE2eMockForecast();
  raw.drawing_id = DEMO_DRAWING_ID;
  raw.job_id = "demo-job-slicrank";
  raw.document_name = DEMO_STEP_NAME;

  const mesh = await fetchDemoMesh();
  if (mesh?.vertices?.length && mesh?.indices?.length) {
    const prevDerived =
      (raw.dxf_features?.derived as Record<string, unknown> | undefined) || {};
    raw.dxf_features = {
      ...(raw.dxf_features || {}),
      classification: "assembly_or_multi_solid",
      source: "demo_step",
      step_path: DEMO_STEP_NAME,
      derived: {
        ...prevDerived,
        mesh,
        is_real_3d: true,
        solid_count: mesh.groups?.length ?? 2,
        occ_ok: true,
        volume_m3: prevDerived.volume_m3 ?? 0.00094,
        complexity_score: prevDerived.complexity_score ?? 22,
      },
    };
  }

  return enrichForecast(raw);
}
